// src/pages/GameDetail.jsx — Enhanced: image gallery, mobile appbar, bottom action bar
import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import { getGame } from "../services/gameService";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguagesContext";

const ACC_CFG = {
  steam:        { label:"Steam",        icon:"🎮", color:"#1b9aff", bg:"rgba(27,154,255,0.08)",  border:"rgba(27,154,255,0.2)",  desc:"Shared Steam account. Login, install the game, switch to Offline Mode and play." },
  epic:         { label:"Epic Games",   icon:"⬛", color:"#e0e0e0", bg:"rgba(224,224,224,0.06)", border:"rgba(224,224,224,0.15)", desc:"Epic Games shared account. Download and play in offline mode." },
  game_account: { label:"Game Account", icon:"👤", color:"#a78bfa", bg:"rgba(167,139,250,0.08)", border:"rgba(167,139,250,0.2)",  desc:"Direct game account with credentials. Full access to the game." },
  shared:       { label:"Shared",       icon:"🔗", color:"#34d399", bg:"rgba(52,211,153,0.06)",  border:"rgba(52,211,153,0.18)", desc:"Shared account optimized for offline gameplay." },
  private:      { label:"Private",      icon:"🔒", color:"#fbbf24", bg:"rgba(251,191,36,0.08)",  border:"rgba(251,191,36,0.2)",  desc:"Private account assigned exclusively to you." },
  family:       { label:"Family Share", icon:"👨‍👩‍👧", color:"#f97316", bg:"rgba(249,115,22,0.08)", border:"rgba(249,115,22,0.2)",  desc:"Steam Family Share access without sharing credentials." },
};

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
.gd { min-height:100vh; background:#121212; color:#fff; font-family:'Inter',sans-serif; }

/* ─── IMAGE LIGHTBOX ─── */
.lightbox {
  position:fixed; inset:0; z-index:2000;
  background:rgba(0,0,0,0.95);
  display:flex; align-items:center; justify-content:center;
  animation:lbIn 0.18s ease;
}
@keyframes lbIn { from{opacity:0} to{opacity:1} }
.lightbox-img {
  max-width:92vw; max-height:88vh;
  object-fit:contain; border-radius:8px;
  box-shadow:0 32px 80px rgba(0,0,0,0.8);
}
.lightbox-close {
  position:absolute; top:20px; right:20px;
  width:40px; height:40px; border-radius:50%;
  background:rgba(255,255,255,0.1); border:1px solid rgba(255,255,255,0.15);
  color:#fff; font-size:18px; cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  transition:background 0.15s;
}
.lightbox-close:hover { background:rgba(255,255,255,0.2); }
.lightbox-nav {
  position:absolute; top:50%; transform:translateY(-50%);
  width:44px; height:44px; border-radius:50%;
  background:rgba(255,255,255,0.08); border:1px solid rgba(255,255,255,0.12);
  color:#fff; font-size:20px; cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  transition:background 0.15s;
}
.lightbox-nav:hover { background:rgba(255,255,255,0.18); }
.lightbox-nav.prev { left:20px; }
.lightbox-nav.next { right:20px; }
.lightbox-counter {
  position:absolute; bottom:20px; left:50%; transform:translateX(-50%);
  font-size:13px; color:rgba(255,255,255,0.5); font-family:'Inter',sans-serif;
}

/* ─── HERO BANNER ─── */
.gd-banner { position:relative; height:500px; overflow:hidden; margin-top:60px; }
.gd-banner-img {
  width:100%; height:100%; object-fit:cover;
  filter:brightness(0.3) saturate(0.5);
  transform:scale(1.05);
  transition:transform 8s ease;
}
.gd-banner:hover .gd-banner-img { transform:scale(1.0); }
.gd-banner-ph { width:100%; height:100%; background:linear-gradient(135deg,#1a1a1a,#2a2a2a); display:flex; align-items:center; justify-content:center; font-size:80px; }
.gd-banner-grad { position:absolute; inset:0; background:linear-gradient(to top,#121212 0%,rgba(18,18,18,0.55) 55%,transparent 100%); }
.gd-banner-content { position:absolute; bottom:0; left:0; right:0; max-width:1400px; margin:0 auto; padding:40px 80px; }
.gd-back { display:inline-flex; align-items:center; gap:6px; font-size:14px; font-weight:500; color:rgba(255,255,255,0.5); text-decoration:none; margin-bottom:20px; transition:color 0.15s; }
.gd-back:hover { color:#fff; }
.gd-tags { display:flex; gap:6px; margin-bottom:14px; flex-wrap:wrap; }
.gd-tag { font-size:11px; font-weight:600; color:#0078F2; background:rgba(0,120,242,0.1); border:1px solid rgba(0,120,242,0.25); border-radius:4px; padding:3px 10px; text-transform:uppercase; letter-spacing:0.5px; }
.gd-title { font-size:clamp(28px,5vw,52px); font-weight:900; letter-spacing:-1.5px; color:#fff; margin-bottom:14px; text-shadow:0 2px 20px rgba(0,0,0,0.5); }
.gd-meta { display:flex; gap:12px; align-items:center; flex-wrap:wrap; }
.gd-rating { font-size:14px; font-weight:600; color:#eab308; display:flex; align-items:center; gap:5px; }
.gd-stock { font-size:12px; font-weight:600; padding:5px 12px; border-radius:4px; }
.gd-stock.in  { background:rgba(34,197,94,0.12);  color:#22c55e; border:1px solid rgba(34,197,94,0.25); }
.gd-stock.low { background:rgba(234,179,8,0.12);  color:#eab308; border:1px solid rgba(234,179,8,0.25); }
.gd-stock.out { background:rgba(239,68,68,0.1);   color:#ef4444; border:1px solid rgba(239,68,68,0.2); }

/* ─── IMAGE PREVIEW GALLERY ─── */
.gd-gallery {
  max-width:1400px; margin:0 auto;
  padding:28px 80px 0;
}
.gd-gallery-title {
  font-size:11px; font-weight:700; color:#3a3a3a;
  text-transform:uppercase; letter-spacing:1.5px; margin-bottom:14px;
}
.gd-gallery-grid {
  display:grid;
  grid-template-columns:2fr 1fr 1fr;
  gap:8px;
  border-radius:12px; overflow:hidden;
}
.gd-gallery-main {
  position:relative; cursor:pointer; overflow:hidden; border-radius:8px;
  aspect-ratio:16/9; background:#1a1a1a;
  grid-row: span 2;
}
.gd-gallery-main img {
  width:100%; height:100%; object-fit:cover;
  transition:transform 0.4s ease, filter 0.3s;
}
.gd-gallery-main:hover img { transform:scale(1.04); filter:brightness(1.1); }
.gd-gallery-thumb {
  position:relative; cursor:pointer; overflow:hidden; border-radius:8px;
  aspect-ratio:16/9; background:#1a1a1a;
}
.gd-gallery-thumb img {
  width:100%; height:100%; object-fit:cover;
  transition:transform 0.3s ease, filter 0.3s;
}
.gd-gallery-thumb:hover img { transform:scale(1.06); filter:brightness(1.1); }
.gd-gallery-overlay {
  position:absolute; inset:0;
  background:rgba(0,0,0,0); border-radius:8px;
  display:flex; align-items:center; justify-content:center;
  transition:background 0.2s;
}
.gd-gallery-main:hover .gd-gallery-overlay,
.gd-gallery-thumb:hover .gd-gallery-overlay {
  background:rgba(0,120,242,0.15);
}
.gd-gallery-zoom {
  opacity:0; transform:scale(0.7);
  transition:all 0.2s;
  width:40px; height:40px; border-radius:50%;
  background:rgba(0,0,0,0.6); border:1px solid rgba(255,255,255,0.2);
  display:flex; align-items:center; justify-content:center; color:#fff; font-size:16px;
}
.gd-gallery-main:hover .gd-gallery-zoom,
.gd-gallery-thumb:hover .gd-gallery-zoom { opacity:1; transform:scale(1); }
.gd-gallery-more {
  position:absolute; inset:0; background:rgba(0,0,0,0.65);
  display:flex; align-items:center; justify-content:center; border-radius:8px;
  font-family:'Inter',sans-serif; font-size:18px; font-weight:800; color:#fff;
  cursor:pointer;
}
/* Single cover fallback */
.gd-gallery-single {
  border-radius:12px; overflow:hidden;
  aspect-ratio:16/7; cursor:pointer; position:relative;
  background:#1a1a1a;
}
.gd-gallery-single img { width:100%; height:100%; object-fit:cover; transition:filter 0.3s; }
.gd-gallery-single:hover img { filter:brightness(1.08); }

/* ─── BODY LAYOUT ─── */
.gd-body { max-width:1400px; margin:0 auto; padding:32px 80px 80px; display:grid; grid-template-columns:1fr 340px; gap:48px; align-items:start; }

/* ─── LEFT CONTENT ─── */
.gd-sect { margin-bottom:36px; }
.gd-sect-title { font-size:11px; font-weight:700; color:#3a3a3a; text-transform:uppercase; letter-spacing:1.5px; margin-bottom:16px; padding-bottom:10px; border-bottom:1px solid rgba(255,255,255,0.05); }
.gd-desc { font-size:15px; color:#8a8a8a; line-height:1.85; font-weight:400; }

.acc-block { border-radius:10px; padding:18px 20px; display:flex; gap:16px; align-items:flex-start; margin-bottom:32px; }
.acc-icon { font-size:30px; flex-shrink:0; margin-top:2px; }
.acc-label { font-size:16px; font-weight:700; margin-bottom:5px; }
.acc-desc { font-size:14px; color:#6b6b6b; line-height:1.65; }

.feat-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
.feat-item {
  background:#161616; border:1px solid rgba(255,255,255,0.05); border-radius:8px;
  padding:12px 14px; display:flex; align-items:center; gap:10px;
  font-size:13px; font-weight:500; color:#8a8a8a;
  transition:border-color 0.15s, color 0.15s;
}
.feat-item:hover { border-color:rgba(0,120,242,0.2); color:#c0c0c0; }
.feat-icon { font-size:16px; }

.how-step { display:flex; gap:14px; align-items:flex-start; margin-bottom:12px; }
.how-num { width:24px; height:24px; background:#0078F2; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; color:#fff; flex-shrink:0; margin-top:1px; }
.how-text { font-size:14px; color:#8a8a8a; padding-top:2px; line-height:1.65; }

/* ─── SIDEBAR ─── */
.gd-sidebar { background:#171717; border:1px solid rgba(255,255,255,0.07); border-radius:14px; padding:28px; position:sticky; top:80px; }
.gd-price { font-size:46px; font-weight:900; color:#0078F2; margin-bottom:2px; letter-spacing:-2px; }
.gd-price-sub { font-size:12px; color:#4a4a4a; margin-bottom:24px; font-weight:500; letter-spacing:0.3px; }

.gd-info { margin-bottom:24px; }
.gd-row { display:flex; justify-content:space-between; padding:11px 0; border-bottom:1px solid rgba(255,255,255,0.05); font-size:14px; }
.gd-row:last-child { border-bottom:none; }
.gd-row-label { color:#4a4a4a; font-weight:400; }
.gd-row-val { color:#e0e0e0; font-weight:600; text-align:right; }

.btn-buy {
  width:100%; padding:15px; background:#0078F2; border:none; border-radius:8px;
  font-family:'Inter',sans-serif; font-size:15px; font-weight:700; color:#fff;
  cursor:pointer; transition:all 0.15s; margin-bottom:10px;
  display:flex; align-items:center; justify-content:center; gap:8px;
  box-shadow:0 4px 20px rgba(0,120,242,0.25);
}
.btn-buy:hover { background:#0a84ff; transform:translateY(-1px); box-shadow:0 8px 28px rgba(0,120,242,0.35); }
.btn-buy:disabled { background:#242424; color:#3a3a3a; cursor:not-allowed; transform:none; box-shadow:none; }

.btn-chat-s {
  width:100%; padding:12px; background:rgba(34,197,94,0.08); border:1px solid rgba(34,197,94,0.2);
  border-radius:8px; font-family:'Inter',sans-serif; font-size:14px; font-weight:600; color:#22c55e;
  cursor:pointer; transition:all 0.15s; margin-bottom:20px;
  display:flex; align-items:center; justify-content:center; gap:7px;
}
.btn-chat-s:hover { background:rgba(34,197,94,0.14); border-color:rgba(34,197,94,0.35); }

.secure-note { text-align:center; font-size:11px; color:#3a3a3a; font-weight:500; letter-spacing:0.2px; }

/* ─── SKELETON ─── */
.skel { background:#1a1a1a; border-radius:6px; animation:shim 1.5s ease infinite; }
@keyframes shim { 0%,100%{opacity:.5} 50%{opacity:.8} }

/* ════════════════════════════
   DESKTOP / TABLET
════════════════════════════ */
@media(max-width:1100px) {
  .gd-gallery { padding:24px 40px 0; }
  .gd-body { grid-template-columns:1fr; padding:28px 40px 60px; }
  .gd-sidebar { position:static; order:-1; }
  .gd-banner-content { padding:40px; }
}

/* ════════════════════════════
   MOBILE (≤ 767px)
════════════════════════════ */
@media(max-width:767px) {
  /* Hide desktop navbar on mobile — we use custom appbar */
  .gd .nav { display:none !important; }

  /* ── MOBILE APPBAR ── */
  .gd-appbar {
    display:flex !important;
    position:fixed; top:0; left:0; right:0; z-index:1000;
    height:56px;
    background:rgba(13,13,13,0.96);
    backdrop-filter:blur(20px);
    -webkit-backdrop-filter:blur(20px);
    border-bottom:1px solid rgba(255,255,255,0.06);
    align-items:center;
    padding:0 16px;
    gap:12px;
  }
  .gd-appbar-back {
    width:36px; height:36px; border-radius:50%;
    background:rgba(255,255,255,0.07);
    border:1px solid rgba(255,255,255,0.1);
    display:flex; align-items:center; justify-content:center;
    color:#fff; font-size:18px; cursor:pointer;
    text-decoration:none; flex-shrink:0;
    -webkit-tap-highlight-color:transparent;
    transition:background 0.15s;
  }
  .gd-appbar-back:active { background:rgba(255,255,255,0.14); }
  .gd-appbar-title {
    flex:1; font-family:'Inter',sans-serif;
    font-size:15px; font-weight:700; color:#fff;
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  }

  /* Banner adjusts for appbar */
  .gd-banner { height:300px; margin-top:56px; }
  .gd-banner-content { padding:20px 16px; }
  .gd-title { font-size:22px; letter-spacing:-0.5px; }

  /* Gallery on mobile — horizontal scroll */
  .gd-gallery { padding:16px 16px 0; }
  .gd-gallery-grid {
    display:flex; gap:8px;
    overflow-x:auto; padding-bottom:4px;
    scrollbar-width:none;
    grid-template-columns:unset;
  }
  .gd-gallery-grid::-webkit-scrollbar { display:none; }
  .gd-gallery-main {
    flex-shrink:0; width:72vw; aspect-ratio:16/9;
    border-radius:10px; grid-row:unset;
  }
  .gd-gallery-thumb {
    flex-shrink:0; width:40vw; aspect-ratio:16/9; border-radius:8px;
  }

  /* Body */
  .gd-body { padding:20px 16px 120px; gap:20px; }
  .gd-sidebar { border-radius:12px; }
  .feat-grid { grid-template-columns:1fr; }

  /* ── MOBILE BOTTOM ACTION BAR ── */
  .gd-bottom-bar {
    display:flex !important;
    position:fixed; bottom:0; left:0; right:0; z-index:1000;
    padding:12px 16px;
    padding-bottom:calc(12px + env(safe-area-inset-bottom, 0px));
    background:rgba(13,13,13,0.97);
    backdrop-filter:blur(24px);
    -webkit-backdrop-filter:blur(24px);
    border-top:1px solid rgba(255,255,255,0.07);
    gap:10px;
    align-items:center;
  }
  .gd-bottom-price {
    display:flex; flex-direction:column;
    font-family:'Inter',sans-serif; flex-shrink:0;
  }
  .gd-bottom-price-amount {
    font-size:22px; font-weight:900; color:#0078F2; letter-spacing:-0.8px; line-height:1;
  }
  .gd-bottom-price-label {
    font-size:10px; color:#4a4a4a; font-weight:500; letter-spacing:0.2px; margin-top:2px;
  }
  .gd-bottom-actions {
    display:flex; flex:1; gap:8px;
  }
  .btn-bottom-chat {
    flex:1; padding:13px 10px;
    background:rgba(34,197,94,0.1); border:1px solid rgba(34,197,94,0.25);
    border-radius:10px; font-family:'Inter',sans-serif;
    font-size:14px; font-weight:700; color:#22c55e;
    cursor:pointer; display:flex; align-items:center; justify-content:center; gap:6px;
    -webkit-tap-highlight-color:transparent; transition:background 0.15s;
  }
  .btn-bottom-chat:active { background:rgba(34,197,94,0.2); }
  .btn-bottom-buy {
    flex:2; padding:13px 10px;
    background:#0078F2; border:none; border-radius:10px;
    font-family:'Inter',sans-serif; font-size:14px; font-weight:800; color:#fff;
    cursor:pointer; display:flex; align-items:center; justify-content:center; gap:6px;
    -webkit-tap-highlight-color:transparent; transition:background 0.15s;
    box-shadow:0 4px 20px rgba(0,120,242,0.3);
  }
  .btn-bottom-buy:active { background:#0a84ff; }
  .btn-bottom-buy:disabled { background:#242424; color:#444; box-shadow:none; }

  /* Hide sidebar buy/chat buttons on mobile — bottom bar handles them */
  .gd-sidebar .btn-buy,
  .gd-sidebar .btn-chat-s { display:none; }
  .gd-sidebar .gd-price { font-size:32px; }
}
`;

/* ── APPBAR is hidden on desktop ── */
const appbarDesktopHide = { display:"none" };
const bottomBarDesktopHide = { display:"none" };

const IconBack = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const IconChat = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);
const IconBuy = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);
const IconZoom = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
  </svg>
);

export default function GameDetail() {
  const { gameId }  = useParams();
  const { user }    = useAuth();
  const { t }       = useLang();
  const navigate    = useNavigate();

  const [game,       setGame]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [liked,      setLiked]      = useState(false);
  const [likes,      setLikes]      = useState(0);
  const [lightbox,   setLightbox]   = useState(null); // index or null
  const [allImages,  setAllImages]  = useState([]);

  useEffect(() => {
    getGame(gameId).then(g => {
      setGame(g);
      setLikes(g?.likes || 0);
      // Build image array: cover + any extra images
      const imgs = [];
      if (g?.coverImage) imgs.push(g.coverImage);
      if (g?.images?.length) imgs.push(...g.images);
      // If only cover, duplicate for gallery effect (still shows the image clearly)
      setAllImages(imgs);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [gameId]);

  const handleBuy = () => {
    if (!user) { navigate("/login", { state:{ from:`/checkout/${gameId}` } }); return; }
    navigate(`/checkout/${gameId}`);
  };
  const handleChat = () => navigate(`/orders`);
  const handleLike = () => setLiked(p => { setLikes(c => c + (p ? -1 : 1)); return !p; });

  const openLightbox = (idx) => setLightbox(idx);
  const closeLightbox = () => setLightbox(null);
  const prevImg = () => setLightbox(i => (i - 1 + allImages.length) % allImages.length);
  const nextImg = () => setLightbox(i => (i + 1) % allImages.length);

  useEffect(() => {
    if (lightbox === null) return;
    const fn = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prevImg();
      if (e.key === "ArrowRight") nextImg();
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [lightbox]);

  const isOOS = !game || game.stock <= 0;
  const acc   = ACC_CFG[game?.accountType] || ACC_CFG.steam;

  return (
    <>
      <style>{S}</style>
      <div className="gd">

        {/* Desktop navbar */}
        <Navbar />

        {/* ── MOBILE APPBAR ── */}
        <div className="gd-appbar" style={appbarDesktopHide}>
          <Link to="/store" className="gd-appbar-back"><IconBack /></Link>
          <div className="gd-appbar-title">{game?.title || "Game Detail"}</div>
        </div>

        {/* Lightbox */}
        {lightbox !== null && allImages[lightbox] && (
          <div className="lightbox" onClick={closeLightbox}>
            <img
              src={allImages[lightbox]}
              alt="Preview"
              className="lightbox-img"
              onClick={e => e.stopPropagation()}
            />
            <button className="lightbox-close" onClick={closeLightbox}>✕</button>
            {allImages.length > 1 && (
              <>
                <button className="lightbox-nav prev" onClick={e => { e.stopPropagation(); prevImg(); }}>‹</button>
                <button className="lightbox-nav next" onClick={e => { e.stopPropagation(); nextImg(); }}>›</button>
                <div className="lightbox-counter">{lightbox + 1} / {allImages.length}</div>
              </>
            )}
          </div>
        )}

        {loading ? (
          <>
            <div className="skel" style={{ height:480, borderRadius:0, marginTop:60 }} />
            <div style={{ maxWidth:1400, margin:"0 auto", padding:"32px 80px", display:"grid", gridTemplateColumns:"1fr 340px", gap:48 }}>
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                {[...Array(6)].map((_,i) => <div key={i} className="skel" style={{ height:14, width:i%2===0?"100%":"65%" }} />)}
              </div>
              <div className="skel" style={{ height:380, borderRadius:12 }} />
            </div>
          </>
        ) : !game ? (
          <div style={{ textAlign:"center", padding:"220px 24px", color:"#6b6b6b" }}>
            <div style={{ fontSize:48, marginBottom:16 }}>😕</div>
            <div style={{ fontSize:20, fontWeight:700, color:"#a3a3a3", marginBottom:12 }}>Game not found</div>
            <Link to="/store" style={{ color:"#0078F2", fontSize:14 }}>{t("back_store")}</Link>
          </div>
        ) : (
          <>
            {/* ── HERO BANNER ── */}
            <div className="gd-banner">
              {game.coverImage
                ? <img src={game.coverImage} alt={game.title} className="gd-banner-img" />
                : <div className="gd-banner-ph">🎮</div>
              }
              <div className="gd-banner-grad" />
              <div className="gd-banner-content">
                <Link to="/store" className="gd-back">← {t("back_store")}</Link>
                <div className="gd-tags">
                  {(Array.isArray(game.genre) ? game.genre : [game.genre]).filter(Boolean).map(g =>
                    <span key={g} className="gd-tag">{g}</span>
                  )}
                </div>
                <h1 className="gd-title">{game.title}</h1>
                <div className="gd-meta">
                  {game.rating && <span className="gd-rating">★ {game.rating} / 5.0</span>}
                  <span className={`gd-stock ${isOOS ? "out" : game.stock <= 2 ? "low" : "in"}`}>
                    {isOOS ? t("out_of_stock") : game.stock <= 2 ? `Only ${game.stock} left!` : "✓ In Stock"}
                  </span>
                </div>
              </div>
            </div>

            {/* ── IMAGE PREVIEW GALLERY ── */}
            {allImages.length > 0 && (
              <div className="gd-gallery">
                {allImages.length === 1 ? (
                  // Single image — show it large and clear
                  <div className="gd-gallery-single" onClick={() => openLightbox(0)}>
                    <img src={allImages[0]} alt={game.title} />
                    <div className="gd-gallery-overlay">
                      <div className="gd-gallery-zoom"><IconZoom /></div>
                    </div>
                  </div>
                ) : (
                  // Multiple images — grid layout
                  <div className="gd-gallery-grid">
                    {/* Main large image */}
                    <div className="gd-gallery-main" onClick={() => openLightbox(0)}>
                      <img src={allImages[0]} alt={`${game.title} 1`} />
                      <div className="gd-gallery-overlay">
                        <div className="gd-gallery-zoom"><IconZoom /></div>
                      </div>
                    </div>
                    {/* Up to 4 thumbnails */}
                    {allImages.slice(1, 5).map((img, i) => (
                      <div key={i} className="gd-gallery-thumb" onClick={() => openLightbox(i + 1)}>
                        <img src={img} alt={`${game.title} ${i + 2}`} />
                        <div className="gd-gallery-overlay">
                          {/* Show "+N more" on last thumb if there are more images */}
                          {i === 3 && allImages.length > 5 ? (
                            <div className="gd-gallery-more">+{allImages.length - 5}</div>
                          ) : (
                            <div className="gd-gallery-zoom"><IconZoom /></div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── BODY ── */}
            <div className="gd-body">
              {/* Left content */}
              <div>
                {/* Account type */}
                <div className="acc-block" style={{ background:acc.bg, border:`1px solid ${acc.border}` }}>
                  <div className="acc-icon">{acc.icon}</div>
                  <div>
                    <div className="acc-label" style={{ color:acc.color }}>{acc.label}</div>
                    <div className="acc-desc">{acc.desc}</div>
                  </div>
                </div>

                <div className="gd-sect">
                  <div className="gd-sect-title">{t("about_game")}</div>
                  <p className="gd-desc">{game.description || "No description available."}</p>
                </div>

                <div className="gd-sect">
                  <div className="gd-sect-title">{t("whats_included")}</div>
                  <div className="feat-grid">
                    {[["⚡",t("instant_delivery")],["🔒",t("offline_access")],["🎮",t("full_access")],["💬",t("support_chat")],["🔄",t("permanent")],["🇰🇭",t("cambodia")]].map(([icon,label]) => (
                      <div key={label} className="feat-item"><span className="feat-icon">{icon}</span>{label}</div>
                    ))}
                  </div>
                </div>

                <div className="gd-sect">
                  <div className="gd-sect-title">{t("how_works")}</div>
                  {["Buy & receive credentials instantly","Login to Steam/Epic with provided account","Install the game from your Library","Switch to Offline Mode","Play anytime, anywhere 🎉"].map((s, i) => (
                    <div key={i} className="how-step">
                      <div className="how-num">{i + 1}</div>
                      <div className="how-text">{s}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── SIDEBAR ── */}
              <div className="gd-sidebar">
                <div className="gd-price">${game.price?.toFixed(2)}</div>
                <div className="gd-price-sub">USD · ONE-TIME PAYMENT</div>

                <div className="gd-info">
                  <div className="gd-row">
                    <span className="gd-row-label">{t("delivery_label")}</span>
                    <span className="gd-row-val" style={{ color:"#22c55e" }}>⚡ {t("delivery_val")}</span>
                  </div>
                  <div className="gd-row">
                    <span className="gd-row-label">{t("type_label")}</span>
                    <span className="gd-row-val" style={{ color:acc.color }}>{acc.icon} {acc.label}</span>
                  </div>
                  <div className="gd-row">
                    <span className="gd-row-label">{t("stock_label")}</span>
                    <span className="gd-row-val">{isOOS ? "❌ " + t("out_of_stock") : "✅ " + t("in_stock")}</span>
                  </div>
                  <div className="gd-row">
                    <span className="gd-row-label">{t("payment_label")}</span>
                    <span className="gd-row-val">Bakong KHQR</span>
                  </div>
                </div>

                {/* Desktop buttons */}
                <button className="btn-buy" onClick={handleBuy} disabled={isOOS}>
                  {isOOS ? t("out_of_stock") : <>{t("buy_now")} <IconBuy /></>}
                </button>
                <button className="btn-chat-s" onClick={handleChat}>
                  <IconChat /> Chat with us
                </button>
                <div className="secure-note">🔒 {t("secured")}</div>
              </div>
            </div>

            {/* ── MOBILE BOTTOM ACTION BAR ── */}
            <div className="gd-bottom-bar" style={bottomBarDesktopHide}>
              <div className="gd-bottom-price">
                <span className="gd-bottom-price-amount">${game.price?.toFixed(2)}</span>
                <span className="gd-bottom-price-label">USD</span>
              </div>
              <div className="gd-bottom-actions">
                <button className="btn-bottom-chat" onClick={handleChat}>
                  <IconChat /> Chat
                </button>
                <button className="btn-bottom-buy" onClick={handleBuy} disabled={isOOS}>
                  {isOOS ? "Out of Stock" : <>{t("buy_now")} <IconBuy /></>}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}